import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import { 
  Home, Calendar, CreditCard, Bell, 
  Settings, LogOut, X
} from 'lucide-react'
import Avatar from '../ui/Avatar'

export default function MobileSidebar({ isOpen, onClose }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { role, canAccessRoute } = usePermissions()

  // Navegación para DUEÑO
  const duenoNavigation = [
    { name: 'Inicio', href: '/', icon: Home },
    { name: 'Calendario', href: '/citas', icon: Calendar },
    { name: 'Clínicas', href: '/suscripciones-clinicas', icon: CreditCard },
    { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
    { name: 'Perfil', href: '/perfil', icon: Settings },
  ]

  // Navegación para PET_SITTER
  const petSitterNavigation = [
    { name: 'Inicio', href: '/', icon: Home },
    { name: 'Calendario', href: '/citas', icon: Calendar },
    { name: 'Clínicas', href: '/suscripciones-clinicas', icon: CreditCard },
    { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
    { name: 'Perfil', href: '/perfil', icon: Settings },
  ]

  const navigationBase =
    role === 'DUEÑO'
      ? duenoNavigation
      : role === 'PET_SITTER'
        ? petSitterNavigation
        : [
            { name: 'Inicio', href: '/', icon: Home },
            { name: 'Agenda', href: '/agenda', icon: Calendar },
            { name: 'Citas', href: '/citas', icon: Calendar },
            { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
            { name: 'Perfil', href: '/perfil', icon: Settings },
          ]
  const navigation = navigationBase.filter((item) => {
    if (item.href === '/') return true
    return canAccessRoute(item.href)
  })

  const handleLogout = () => {
    logout()
    onClose()
  }

  return (
    <>
      {/* Overlay - Solo en mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-slate-800
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:static md:z-auto
          flex flex-col
          shadow-strong md:shadow-none border-r border-pastel-pink
        `}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal={isOpen ? "true" : "false"}
        aria-label="Navigation menu"
      >
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
          className="absolute top-4 right-4 p-2 rounded-full bg-pastel-cream hover:bg-pastel-pink/40 text-pastel-ink transition-colors z-10 md:hidden"
          aria-label="Cerrar menú"
          type="button"
        >
          <X className="w-5 h-5" />
        </button>

        {/* User Profile Section */}
        <div className="pt-6 md:pt-6 pb-6 px-6 border-b border-pastel-pink">
          <div className="flex items-center space-x-4">
            <Avatar 
              user={user} 
              size="xl" 
              showStatus={true}
              className="border-pastel-pink"
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-pastel-ink font-semibold text-base truncate">
                {user?.nombre} {user?.apellido || ''}
              </h3>
              <p className="text-slate-500 text-xs mt-0.5">Cuenta activa</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href === '/' && location.pathname === '/')
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => {
                  // Solo cerrar en mobile
                  if (window.innerWidth < 768) {
                    onClose()
                  }
                }}
                className={`
                  flex items-center space-x-4 px-4 py-3 rounded-xl transition-all duration-200
                  ${isActive 
                    ? 'bg-gradient-to-r from-pastel-teal/15 to-primary-100/60 text-pastel-teal shadow-soft' 
                    : 'text-pastel-ink hover:bg-pastel-cream'
                  }
                `}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-pastel-teal' : 'text-slate-500'}`} />
                <span className="font-medium">{item.name}</span>
              </Link>
            )
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="px-4 py-6 border-t border-pastel-pink">
          <div className="flex items-center space-x-0">
            <button
              onClick={() => {
                navigate('/perfil')
                if (window.innerWidth < 768) {
                  onClose()
                }
              }}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-xl text-pastel-ink hover:bg-pastel-cream transition-all duration-200"
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">Perfil</span>
            </button>
            <div className="w-px h-8 bg-pastel-pink mx-2"></div>
            <button
              onClick={() => {
                handleLogout()
                if (window.innerWidth < 768) {
                  onClose()
                }
              }}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-xl text-pastel-ink hover:bg-pastel-cream transition-all duration-200"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Salir</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
